import { useEffect, useState, type ComponentType } from 'react'
import { router, usePage } from '@inertiajs/react'

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/atoms/Accordion'
import { type NavTree } from '@components/layouts/sidebar/navTree'
import { CallbackNotifications } from '@frontend/contexts/previews/components/CallbackNotifications'
import CallbackRequestsCallListPreview from '@frontend/contexts/previews/pages/CallbackRequestsCallListPreview'
import CallbackRequestsPreview from '@frontend/contexts/previews/pages/CallbackRequestsPreview'
import CallingSessionCallbackPreview from '@frontend/contexts/previews/pages/CallingSessionCallbackPreview'
import CallingSessionV2CallbackPreview from '@frontend/contexts/previews/pages/CallingSessionV2CallbackPreview'

type DemoPage = { component: ComponentType; notes: string[]; why?: string; rough?: boolean }

const PAGES: Record<string, DemoPage> = {
  'calling-session': {
    component: CallingSessionCallbackPreview,
    notes: [
      'Log contact gets a "Callback requested" status. Picking it opens the schedule-callback fields inside the dialog, so the leader books the callback without leaving the call.',
      'Date and time, with "Call is flexible" for a one-hour window. A past time is rejected.',
      '"Don\'t call them from anywhere else until this is done", on by default.',
      'Who calls back: Me or Any group leader. Admins can pick a named person.',
      "An open callback sits at the top of the supporter card: when, who's calling back, and the note, so the caller sees it before dialling.",
      'The contacts log shows the same callback under the call that booked it.',
      'Contact outcome (Successful or Unsuccessful) is back, asked once "No further contact attempts needed" is ticked, as on the live form.',
      'While a callback is open the status badge reads "Skipped · Callback …", the same as on the calling session. "Skip in this session" in the callback card puts them back in the queue when turned off, and the badge turns to "Available for call". Turn it back on to undo.',
    ],
    why: 'The callback is booked in the same moment as the call outcome, so nothing is left to memory.',
  },
  'calling-session-inline': {
    component: CallingSessionV2CallbackPreview,
    notes: [
      'The same flow on the new inline log-call form: outcome tiles, "Callback requested" among them.',
      'The scheduling fields appear under the tile. One button: "Log call and schedule callback".',
      'A supporter with an open callback gets it rescheduled, never a second one.',
      'Ticking "No further contact attempts needed" asks for the contact outcome, as on the live form.',
      'As on Calling Session Supporter, the open callback sits in the supporter card with its "Skip in this session" switch, and the contacts log shows the call that booked it.',
    ],
    why: 'One screen, fewer clicks per call.',
    rough: true,
  },
  'calling-session-dashboard': {
    component: CallbackRequestsCallListPreview,
    notes: [
      "Supporters with an open callback are skipped in this session until it's done. Their row shows the callback time, who is calling back (hover for who asked for it) and the callback note, which expands to read in full.",
      '"Add to calling queue" in the row menu puts them back once the callback is done or cancelled.',
      "Progress counts only supporters in the queue, so those waiting on a callback don't drag it down.",
    ],
    why: "A leader working the list in order can't break a callback promise by accident.",
  },
  calls: {
    component: CallbackRequestsPreview,
    notes: [
      'One Calls page per group: Call lists, Calling sessions and Callback requests tabs.',
      'Callback cards show who and when, how due it is (Overdue, Due now, Today, Due in 3 days), the note, and Call, Reschedule or Manage.',
      "Search, and whose callbacks: Assigned to me or Any leader. Leaders never see another leader's callbacks.",
      'Admins choose whose callbacks from one Showing list: Assigned to me, Any leader, Everyone, or a named person. Picking a person, say a leader who is off sick, shows only theirs to reassign. On Everyone, the Assigned to column sorts and groups the list by person.',
      "Each callback's menu has SMS and Email next to Log contact, to follow up however suits the supporter.",
      'Reminders in the sidebar counts the callbacks that are due or overdue, yours and unassigned. It opens a list of those and the ones coming up, and each one opens here, highlighted.',
      "Schedule callback from the header, for a callback that didn't come from a call.",
      'Add to calendar per card, or all at once from the Calendar menu above the list: subscribe so they stay in sync, or download the ones shown.',
    ],
  },
}

const DEMO_PATH = '/demo/callbacks/'
const REMINDERS_HASH = '#reminders'

const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement && (target.isContentEditable || /^(input|textarea|select)$/i.test(target.tagName))

export default function CallbacksDemoShow({ page }: { page: string }) {
  const { component: Preview, notes, why, rough } = PAGES[page]
  const { url, props } = usePage<{ navTree?: NavTree }>()
  const steps = props.navTree?.sections ?? []
  const [notificationsAnchor, setNotificationsAnchor] = useState<HTMLAnchorElement | null>(null)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const index = steps.findIndex((step) => step.key === page)
  const previous = steps[index - 1]
  const next = steps[index + 1]

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target)) return
      if (document.querySelector('[role="dialog"]')) return
      const target = event.key === 'ArrowRight' ? next : event.key === 'ArrowLeft' ? previous : undefined
      if (target?.href) router.visit(target.href)
    }

    const onClick = (event: MouseEvent) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return
      const anchor = (event.target as HTMLElement).closest<HTMLAnchorElement>('[data-sidebar="sidebar"] a[href]')
      if (anchor?.hash === REMINDERS_HASH) {
        event.preventDefault()
        setNotificationsAnchor(anchor)
        setNotificationsOpen((open) => !open)
        return
      }
      if (!anchor || !anchor.pathname.includes(DEMO_PATH)) return
      event.preventDefault()
      router.visit(anchor.href)
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('click', onClick)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('click', onClick)
    }
  }, [previous, next])

  return (
    <>
      {rough && (
        <p className="tw:m-0 tw:flex tw:flex-wrap tw:items-center tw:justify-center tw:gap-x-2 tw:gap-y-1 tw:border-b tw:border-warning-200 tw:bg-warning-100 tw:px-6 tw:py-2 tw:text-center tw:text-sm tw:text-warning-900">
          <span className="tw:rounded tw:bg-warning-700 tw:px-1.5 tw:py-0.5 tw:text-xs tw:font-semibold tw:text-white">
            Early sketch
          </span>
          We put this form together quickly. Tell us if the direction is right before we refine it.
        </p>
      )}
      <Preview key={url} />
      <CallbackNotifications
        anchor={notificationsAnchor}
        callsHref={steps.find((step) => step.key === 'calls')?.href ?? ''}
        open={notificationsOpen}
        onOpenChange={setNotificationsOpen}
      />
      <Accordion type="single" collapsible className="tw:border-t tw:border-gray-200 tw:bg-gray-cool-50 tw:px-6">
        <AccordionItem value="notes" className="tw:border-b-0">
          <AccordionTrigger className="tw:text-gray-600 tw:hover:no-underline">What&apos;s new here</AccordionTrigger>
          <AccordionContent>
            <ul className="tw:max-w-3xl tw:list-disc tw:space-y-1.5 tw:pl-5 tw:text-gray-900">
              {notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
            {why && <p className="tw:mt-3 tw:max-w-3xl tw:italic tw:text-gray-600">Why: {why}</p>}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  )
}
